import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameLayout } from './GameLayout';
import { difficultyLabel, useQuizzes, type Difficulty, type Lang } from '../data/quizzes';
import { buildQuizPack, packQuestionsCount, QUIZ_PACK_DEFS } from '../data/quizPacks';
import { api } from '../lib/api';
import { QUIZ_TEXT, useQuizLang } from '../lib/quizLang';

export function QuizLive() {
  const navigate = useNavigate();
  const [lang] = useQuizLang();
  const t = QUIZ_TEXT[lang];
  const { quizzes } = useQuizzes(lang);
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(() => new Map(quizzes.map((q) => [q.id, q])), [quizzes]);

  async function createRoom(packId: string) {
    const def = QUIZ_PACK_DEFS.find((d) => d.id === packId);
    if (!def) return;
    const pack = buildQuizPack(def, quizzes, lang);
    if (!pack) {
      setError(t.packs.loadError);
      return;
    }
    setBusyPack(packId);
    setError(null);
    try {
      const res = await api.createQuizRoom({ pack });
      navigate(`/quiz-room/${res.code}`);
    } catch (e) {
      setError((e as Error).message);
      setBusyPack(null);
    }
  }

  return (
    <GameLayout>
      <QuizLiveStyles />
      <div className="qz-topline">
        <span className="label">{t.packs.crumb(QUIZ_PACK_DEFS.length)}</span>
      </div>
      <h1 className="ql-title">
        {t.packs.titleA} <span className="accent-word">{t.packs.titleB}</span>
      </h1>
      <p className="qz-lede">{t.packs.lede}</p>

      <div className="ql-grid">
        {QUIZ_PACK_DEFS.map((def) => {
          return (
            <button
              key={def.id}
              className="ql-card"
              onClick={() => createRoom(def.id)}
              disabled={busyPack !== null}
            >
              <div className="ql-card-top">
                <span className="ql-emoji">{def.emoji}</span>
                <div>
                  <h3>{def.titles[lang as Lang]}</h3>
                  <span className="ql-meta">
                    {t.packs.levels(def.quizIds.length)} · {t.questions(packQuestionsCount(def))}
                  </span>
                </div>
              </div>
              <p className="ql-desc">{def.descs[lang as Lang]}</p>
              <div className="ql-levels">
                {def.quizIds.map((id, i) => {
                  const quiz = byId.get(id);
                  const diff = (quiz?.difficulty ?? 'medium') as Difficulty;
                  return (
                    <span key={id} className={`ql-level ${diff}`}>
                      <span className="ql-level-n">{i + 1}</span>
                      <span className="ql-level-name">{quiz ? quiz.title : id}</span>
                      <span className="ql-level-diff">{difficultyLabel(diff, lang)}</span>
                    </span>
                  );
                })}
              </div>
              <span className="ql-cta">
                {busyPack === def.id ? '…' : t.packs.create}
              </span>
            </button>
          );
        })}
      </div>

      {error && <p className="ql-error">{error}</p>}
    </GameLayout>
  );
}

function QuizLiveStyles() {
  return (
    <style>{`
      .ql-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 72px; line-height: 1.02; letter-spacing: -.015em; margin: 12px 0 0; }

      .ql-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 40px; }
      .ql-card { display: flex; flex-direction: column; gap: 14px; text-align: left; padding: 24px;
        background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); color: var(--ink);
        cursor: pointer; transition: border-color .2s, transform .2s; font: inherit; }
      .ql-card:hover:not(:disabled) { border-color: var(--accent); transform: translateY(-2px); }
      .ql-card:disabled { opacity: .6; cursor: wait; }

      .ql-card-top { display: flex; align-items: center; gap: 16px; }
      .ql-emoji { font-size: 40px; line-height: 1; }
      .ql-card h3 { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 30px; line-height: 1; margin: 0 0 7px; letter-spacing: -.005em; }
      .ql-meta { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--ink-faint); }

      .ql-desc { font-size: 14px; line-height: 1.5; color: var(--ink-dim); margin: 0; }

      .ql-levels { display: flex; flex-direction: column; gap: 6px; }
      .ql-level { display: grid; grid-template-columns: 22px 1fr auto; align-items: center; gap: 10px;
        font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .04em;
        border: 1px solid var(--line); border-radius: 4px; padding: 8px 10px; color: var(--ink-dim); }
      .ql-level-n { width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center;
        border-radius: 3px; background: var(--line); color: var(--ink); font-size: 10px; }
      .ql-level.easy .ql-level-n { background: rgba(126, 201, 107, .25); }
      .ql-level.medium .ql-level-n { background: rgba(224, 195, 106, .25); }
      .ql-level.hard .ql-level-n { background: rgba(224, 147, 106, .3); }
      .ql-level.hardcore .ql-level-n { background: rgba(224, 106, 106, .3); }
      .ql-level-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ink); }
      .ql-level-diff { text-transform: uppercase; letter-spacing: .1em; font-size: 9px; color: var(--ink-faint); }

      .ql-cta { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .14em; text-transform: uppercase;
        color: var(--accent); margin-top: auto; }

      .ql-error { color: #e06a6a; font-size: 14px; margin-top: 20px; }

      @media (max-width: 1000px) { .ql-grid { grid-template-columns: repeat(2, 1fr); } .ql-title { font-size: 54px; } }
      @media (max-width: 640px) { .ql-grid { grid-template-columns: 1fr; } .ql-title { font-size: 42px; } }
    `}</style>
  );
}
